import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContextOIDC';
import { useNotification } from '../../context/NotificationContext';
import { useThemeMode } from '../../context/ThemeContext';
import { IMAGES } from '../../constants/assets';
import SectionWrapper from '../SectionWrapper';
import Footer from '../layouts/Footer';

const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const hasAttemptedLogin = useRef(false);

  // Reset der Login-Versuch-Referenz beim Komponenten-Mount
  useEffect(() => {
    hasAttemptedLogin.current = false;
  }, []);

  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const { showNotification } = useNotification();
  const { isDark } = useThemeMode();

  // Weiterleitung für bereits angemeldete Benutzer
  useEffect(() => {
    // Debug: Azure-Redirect erkennen
    const isFromAzureRedirect = window.location.search.includes('code=') ||
      window.location.search.includes('error=') ||
      window.location.hash.includes('access_token');

    if (isFromAzureRedirect) {
      console.log('🔄 LoginPage: Azure-Redirect erkannt, warte auf Authentifizierung...');
    }

    // Weiterleiten wenn:
    // 1. Auth-Loading ist fertig
    // 2. Benutzer ist authentifiziert 
    // 3. Nach Redirect ODER Silent Login
    if (!authLoading && isAuthenticated) {
      console.log('🔄 LoginPage: Weiterleitung zu /dienstleister - Benutzer ist eingeloggt');
      navigate('/dienstleister', { replace: true });
    } else if (!authLoading && !isAuthenticated && isFromAzureRedirect) {
      console.log('⚠️ LoginPage: Azure-Redirect aber nicht authentifiziert - möglicherweise Fehler');
      // Bei Redirect-Problemen: Kurz warten und nochmal prüfen
      setTimeout(() => {
        if (!isAuthenticated) {
          console.log('❌ LoginPage: Nach Azure-Redirect immer noch nicht eingeloggt');
          setError('Anmeldung nach Azure-Redirect fehlgeschlagen. Bitte versuchen Sie es erneut.');
        }
      }, 2000);
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Zeige Loading-Indicator während der Auth-Prüfung
  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    hasAttemptedLogin.current = true;

    try {
      await login();
      // Bei OIDC-Redirect wird diese Zeile nie erreicht, da Redirect erfolgt
      // Kein success-Check nötig, da login() void zurückgibt
    } catch (error) {
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default'
      }}
    >
      {/* Main content area */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2
        }}
      >
        <SectionWrapper>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 'calc(100vh - 88px)' // Abzug für Footer
          }}>
            <Paper
              elevation={0}
              sx={{
                padding: 6,
                maxWidth: 450,
                width: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <img
                  src={isDark ? IMAGES.LOGO_DARK : IMAGES.LOGO}
                  alt="DORAsmartCheck.ai"
                  style={{ height: '40px', marginBottom: '1.5rem' }}
                />
                <Typography variant="h4" sx={{ color: 'primary.dark', mb: 1 }}>
                  Anmeldung
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sichere Anmeldung über Azure Active Directory
                  <br />
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                  >
                    (Popup oder neue Seite - je nach Browser-Einstellungen)
                  </Typography>
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                  data-testid="login-azure-submit-button"
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    position: 'relative',
                    mb: 2,
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          marginLeft: '-10px',
                          color: 'inherit'
                        }}
                      />
                      <span style={{ opacity: 0 }}>Mit Azure AD anmelden</span>
                    </>
                  ) : (
                    'Mit Azure AD anmelden'
                  )}
                </Button>

              </form>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Sichere Anmeldung über Microsoft Azure
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Bei Fragen wenden Sie sich an:
                  <br />
                  <Typography
                    component="a"
                    href="mailto:dsc-support@macros-group.com"
                    sx={{
                      color: 'primary.blue',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    dsc-support@macros-group.com
                  </Typography>
                </Typography>
              </Box>
            </Paper>
          </Box>
        </SectionWrapper>
      </Box>

      {/* Footer nur auf der Login-Seite */}
      <Footer />
    </Box>
  );
};

export default LoginPage; 